import { useState, useEffect } from "react";
import { Megaphone } from "lucide-react";
import ChangelogModal from "../ChangelogModal/ChangelogModal";
import { CURRENT_VERSION } from "@/shared/data/changelog";
import { isPregODonnell, isTaylorDay } from "@/shared/utils/firm";
import styles from "./ChangelogButton.module.css";

const STORAGE_KEY = "hourglass_changelog_auto_show";
const MAX_AUTO_SHOWS = 2;

interface Props {
  className?: string;
  variant?: "primary" | "secondary" | "icon";
  text?: string;
}

export default function ChangelogButton({
  className = "",
  variant = "secondary",
  text = "What's New",
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasNew, setHasNew] = useState(false);

  useEffect(() => {
    // Skip auto-show and badge for excluded firms
    if (isPregODonnell() || isTaylorDay()) return;

    // Auto-show: open modal automatically for the first N page loads after an update
    try {
      localStorage.removeItem("hourglass_last_seen_changelog"); // clean up old key
      const raw = localStorage.getItem(STORAGE_KEY);
      const stored = raw ? JSON.parse(raw) : null;

      if (!stored || stored.version !== CURRENT_VERSION) {
        // New version detected — reset counter and auto-show
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: CURRENT_VERSION, count: 1, seen: false }));
        setIsOpen(true);
        setHasNew(true);
      } else if (stored.count < MAX_AUTO_SHOWS) {
        // Same version, haven't hit auto-show limit yet
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: CURRENT_VERSION, count: stored.count + 1, seen: stored.seen }));
        setIsOpen(true);
        setHasNew(!stored.seen);
      } else {
        // Auto-show limit reached — badge persists until manual click
        setHasNew(!stored.seen);
      }
    } catch {
      // Corrupted localStorage — skip auto-show, show badge as fallback
      setHasNew(true);
    }
  }, []);

  const handleOpen = () => {
    setIsOpen(true);
    // Mark as manually seen — stops auto-show and clears badge
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: CURRENT_VERSION, count: MAX_AUTO_SHOWS, seen: true }));
    setHasNew(false);
  };

  const buttonClass = variant === "icon"
    ? `${styles.iconBtn} ${className} ${hasNew ? styles.hasNew : ""}`
    : `${styles.btn} ${styles[variant]} ${className} ${hasNew ? styles.hasNew : ""}`;

  return (
    <>
      <button onClick={handleOpen} className={buttonClass} title="What's New" aria-label="What's New">
        <Megaphone size={variant === "icon" ? 20 : 18} />
        {variant !== "icon" && text}
        {hasNew && (variant === "icon" ? <span className={styles.dot} /> : <span className={styles.badge}>New</span>)}
      </button>
      <ChangelogModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
}
